import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deletePromptsById,
  searchPrompts,
  importPromptsFile,
  createPrompts,
  updatePrompts,
} from "../api/prompts";
import { toast } from "react-toastify";
import { handleApiError } from "../api/InstanceApi";
import { useIntl } from "react-intl";

export const usePromptData = (searchParams) => {
  const intl = useIntl();
  const {
    data: promptData,
    isLoading: promptDataLoading,
    error: promptDataError,
    isFetching: isPromptDataFetching,
  } = useQuery({
    queryKey: [
      "prompt-data",
      searchParams?.page,
      searchParams?.limit,
      searchParams?.name,
      searchParams?.text,
    ],
    queryFn: () => searchPrompts(searchParams),
    staleTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await toast.promise(deletePromptsById(id), {
        pending: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.DELETING" });
          },
        },
        success: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.DELETED" });
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  const importMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      return await toast.promise(importPromptsFile(formData), {
        pending: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.IMPORTING" });
          },
        },
        success: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.IMPORTED" });
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return await toast.promise(createPrompts(data), {
        pending: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.CREATING" });
          },
        },
        success: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.CREATED" });
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      return await toast.promise(updatePrompts(data.id, data), {
        pending: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.UPDATING" });
          },
        },
        success: {
          render() {
            return intl.formatMessage({ id: "PROMPTS.UPDATED" });
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  return {
    promptData,
    promptDataLoading,
    promptDataError,
    isPromptDataFetching,
    deletePromptsByIdAsync: deleteMutation.mutateAsync,
    importPromptsFileAsync: importMutation.mutateAsync,
    createPromptsAsync: createMutation.mutateAsync,
    updatePromptsAsync: updateMutation.mutateAsync,
  };
};
